/* eslint-disable react/no-array-index-key */
/* eslint-disable react-hooks/exhaustive-deps */
import { Check, ChevronDown, Info, Pencil, Plus, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { v4 } from "uuid";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Textarea } from "../../../../components/ui/textarea";
import { cn } from "../../../../lib/utils";
import { useWorkspaceObjectSchema } from "../../../../hooks/QueryHooks";
import { useBusinessContext, useUpdateBusinessContext } from "../../../../hooks/useBusinessContext";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import type {
  BusinessContextPatch,
  EventCatalogItem,
  Feature,
  Goal,
  ICPArchetype,
  LifecycleStage,
  Product,
} from "../../../../types/businessContext";
import notify from "../../../../utils/notify";
import SignalFilterContainer from "../../signals/signal-overview/components/SignalFilterContainer";

/* ------------------------------------------------------------------ */
/*  Lightweight local primitives (replacing Mantine Select / MultiSelect / TagsInput) */
/* ------------------------------------------------------------------ */

interface ComboOption {
  value: string;
  label: string;
}

const SearchableSelect = ({
  label: fieldLabel,
  placeholder,
  value,
  onChange,
  options,
  clearable,
}: {
  label?: string;
  placeholder?: string;
  value: string | null;
  onChange: (v: string | null) => void;
  options: ComboOption[];
  clearable?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <div className="space-y-1.5">
      {fieldLabel && <Label>{fieldLabel}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex h-8 w-full items-center justify-between rounded-[5px] border border-border-chip bg-card px-2 py-1 text-[13px] ring-offset-background focus:outline-none focus:ring-1 focus:ring-primary",
              !selected && "text-muted-foreground",
            )}
          >
            <span className="truncate">{selected?.label ?? placeholder ?? "Select..."}</span>
            <div className="flex items-center gap-1 shrink-0">
              {clearable && value && (
                <span
                  role="button"
                  tabIndex={-1}
                  className="hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                >
                  <X className="h-3 w-3" />
                </span>
              )}
              <ChevronDown className="h-3.5 w-3.5 opacity-50" />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search..." className="h-8" />
            <CommandList>
              <CommandEmpty>No results.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => {
                      onChange(opt.value === value ? null : opt.value);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn("mr-2 h-3.5 w-3.5", value === opt.value ? "opacity-100" : "opacity-0")} />
                    {opt.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const MultiCombobox = ({
  label: fieldLabel,
  placeholder,
  value,
  onChange,
  options,
}: {
  label?: string;
  placeholder?: string;
  value: string[];
  onChange: (v: string[]) => void;
  options: ComboOption[];
}) => {
  const [open, setOpen] = useState(false);
  const selectedLabels = value.map((v) => options.find((o) => o.value === v)?.label ?? v).join(", ");
  return (
    <div className="space-y-1.5">
      {fieldLabel && <Label>{fieldLabel}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex h-8 w-full items-center justify-between rounded-[5px] border border-border-chip bg-card px-2 py-1 text-[13px] ring-offset-background focus:outline-none focus:ring-1 focus:ring-primary",
              value.length === 0 && "text-muted-foreground",
            )}
          >
            <span className="truncate">{value.length ? selectedLabels : (placeholder ?? "Select...")}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search..." className="h-8" />
            <CommandList>
              <CommandEmpty>No results.</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => {
                  const isSelected = value.includes(opt.value);
                  return (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => {
                        onChange(isSelected ? value.filter((v) => v !== opt.value) : [...value, opt.value]);
                      }}
                    >
                      <Check className={cn("mr-2 h-3.5 w-3.5", isSelected ? "opacity-100" : "opacity-0")} />
                      {opt.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const TagsField = ({
  label: fieldLabel,
  placeholder,
  value,
  onChange,
}: {
  label?: string;
  placeholder?: string;
  value: string[];
  onChange: (v: string[]) => void;
}) => {
  const [input, setInput] = useState("");
  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInput("");
  };
  return (
    <div className="space-y-1.5">
      {fieldLabel && <Label>{fieldLabel}</Label>}
      <div className="flex flex-wrap gap-1.5 min-h-[32px] rounded-[5px] border border-border-chip bg-card px-2 py-1.5 focus-within:ring-1 focus-within:ring-primary focus-within:border-primary">
        {value.map((tag) => (
          <Badge key={tag} variant="secondary" className="gap-1 pr-1">
            {tag}
            <button type="button" onClick={() => onChange(value.filter((t) => t !== tag))} className="hover:text-destructive rounded-sm">
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <input
          className="flex-1 min-w-[120px] bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          placeholder={value.length === 0 ? placeholder : ""}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addTag();
            }
            if (e.key === "Backspace" && !input && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={addTag}
        />
      </div>
    </div>
  );
};

const COLOR_SWATCHES = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e",
  "#10b981", "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7",
];

const ColorField = ({
  label: fieldLabel,
  value,
  onChange,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="space-y-1.5">
    {fieldLabel && <Label>{fieldLabel}</Label>}
    <div className="flex items-center gap-2">
      <div className="flex gap-1 flex-wrap">
        {COLOR_SWATCHES.map((c) => (
          <button
            key={c}
            type="button"
            className={cn("h-6 w-6 rounded-full border-2 transition-all", value === c ? "border-foreground scale-110" : "border-transparent")}
            style={{ backgroundColor: c }}
            onClick={() => onChange(c)}
          />
        ))}
      </div>
      <Input className="w-24 h-7 text-xs" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Preview cards                                                     */
/* ------------------------------------------------------------------ */

interface PreviewCardProps {
  title: string;
  subtitle: string;
  items: Array<{ title: string; subtitle?: string; badge?: string; id?: string }>;
  addButtonText: string;
  onEdit: (id?: string) => void;
  borderColor: string;
}

const PreviewCard = ({ title, subtitle, items, addButtonText, onEdit, borderColor }: PreviewCardProps) => (
  <div className="space-y-3">
    <div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">{subtitle}</p>
    </div>
    <div className="flex flex-wrap gap-3">
      {items.map((item, index) => (
        <div
          key={`${title}-${item.title}-${index}`}
          className="relative w-[260px] h-[140px] shrink-0 cursor-pointer rounded-lg border border-border bg-card p-4 pl-7 transition-all hover:shadow-md"
          onClick={() => onEdit(item?.id)}
        >
          <div className="absolute left-3 top-3 bottom-3 w-[3px] rounded-full" style={{ backgroundColor: borderColor }} />
          <div className="flex flex-col h-full overflow-hidden">
            <p className="text-sm font-semibold text-foreground truncate shrink-0">{item.title}</p>
            {item.subtitle && <p className="mt-1 text-xs text-muted-foreground line-clamp-3 flex-1 break-words">{item.subtitle}</p>}
            <div className="flex items-center justify-between mt-auto pt-1 shrink-0">
              {item.badge ? (
                <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">{item.badge}</span>
              ) : <span />}
              <button type="button" className="text-muted-foreground hover:text-foreground transition-colors" onClick={(e) => { e.stopPropagation(); onEdit(item?.id); }}>
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      ))}
      <div
        className="relative w-[260px] h-[140px] shrink-0 cursor-pointer rounded-lg border border-dashed border-border bg-card transition-all hover:shadow-md hover:bg-muted/30 flex flex-col items-center justify-center gap-2"
        onClick={() => onEdit(undefined)}
      >
        <div className="absolute left-3 top-3 bottom-3 w-[3px] rounded-full opacity-60" style={{ backgroundColor: borderColor }} />
        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
          <Plus className="h-4 w-4 text-muted-foreground" />
        </div>
        <p className="text-xs text-muted-foreground text-center">{addButtonText}</p>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Editor footer                                                     */
/* ------------------------------------------------------------------ */

const EditorFooter = ({ item, onDelete, onCancel, onSave, disabled }: {
  item?: any;
  onDelete: () => void;
  onCancel: () => void;
  onSave: () => void;
  disabled?: boolean;
}) => (
  <div className="flex items-center justify-between pt-4 border-t border-border">
    <div>
      {item && (
        <Button variant="destructive" size="sm" onClick={onDelete}>
          <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          Delete
        </Button>
      )}
    </div>
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
      <Button size="sm" onClick={onSave} disabled={disabled}>{item ? "Update" : "Create"}</Button>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Individual Editors                                                */
/* ------------------------------------------------------------------ */

const IndividualValuePropEditor = ({ item, onSave, onDelete, onCancel }: {
  item?: any;
  onSave: (title: string, description: string) => void;
  onDelete: () => void;
  onCancel: () => void;
}) => {
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  useEffect(() => { setTitle(item?.title || ""); setDescription(item?.description || ""); }, [item?.title, item?.description]);
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Highlight why customers choose you over alternatives. Be specific about the outcome or benefit.</p>
      <div className="space-y-1.5"><Label>Title</Label><Input placeholder="Enter value proposition title" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Describe this value proposition" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
      </div>
      <EditorFooter item={item} onDelete={onDelete} onCancel={onCancel} onSave={() => onSave(title, description)} disabled={!title.trim() || !description.trim()} />
    </div>
  );
};

const IndividualProductEditor = ({ item, onSave, onDelete, onCancel }: {
  item?: any;
  onSave: (name: string) => void;
  onDelete: () => void;
  onCancel: () => void;
}) => {
  const [name, setName] = useState(item?.name || "");
  useEffect(() => { setName(item?.name || ""); }, [item?.name]);
  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Add the products or services your company offers. These help AI understand your catalog.</p>
      <div className="space-y-1.5"><Label>Product Name</Label><Input placeholder="Enter product name" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <EditorFooter item={item} onDelete={onDelete} onCancel={onCancel} onSave={() => onSave(name)} disabled={!name.trim()} />
    </div>
  );
};

const IndividualEventEditor = ({ item, onSave, onDelete, onCancel }: {
  item?: any;
  onSave: (name: string, key: string, description: string, entity: string) => void;
  onDelete: () => void;
  onCancel: () => void;
}) => {
  const [name, setName] = useState(item?.name || "");
  const [eventKey, setEventKey] = useState(item?.event_key || "");
  const [description, setDescription] = useState(item?.description || "");
  const [entityRelation, setEntityRelation] = useState(item?.entity_relation || "users");
  const [metricKey, setMetricKey] = useState<string | null>(item?.event_key || null);

  useEffect(() => {
    setName(item?.name || ""); setEventKey(item?.event_key || ""); setDescription(item?.description || "");
    setEntityRelation(item?.entity_relation || "users"); setMetricKey(item?.event_key || null);
  }, [item?.name, item?.event_key, item?.description, item?.entity_relation]);

  const workspace = useWorkspaceStore((state) => state.workspace);
  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({ workspaceId: workspace?.id, enabled: !!workspace?.id });
  const accountMetrics = workspaceSchema?.metricsByObjectSlug?.accounts || workspaceSchema?.metrics || { list: [], obj: {} };
  const userMetrics = workspaceSchema?.metricsByObjectSlug?.users || workspaceSchema?.metrics || { list: [], obj: {} };
  const metricOptions = useMemo(() => {
    const seen = new Set<string>(); const out: ComboOption[] = [];
    [...(accountMetrics?.list || []), ...(userMetrics?.list || [])].forEach((m: any) => {
      const val = m?.name as string; if (!val || seen.has(val)) return; seen.add(val);
      out.push({ value: val, label: m?.display_name || val });
    });
    return out;
  }, [accountMetrics?.list, userMetrics?.list]);

  const handleMetricSelect = (val: string | null) => {
    setMetricKey(val); if (!val) return; setEventKey(val);
    const acc = accountMetrics?.obj?.[val]; const usr = userMetrics?.obj?.[val];
    setName(acc?.display_name || usr?.display_name || val);
    if (acc) setEntityRelation("accounts"); else if (usr) setEntityRelation("users");
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Events track user and account activity. Map an existing metric or define a custom event key.</p>
      <SearchableSelect label="Metric" placeholder="Select metric to auto-fill" value={metricKey} onChange={handleMetricSelect} options={metricOptions} clearable />
      <div className="space-y-1.5"><Label>Event Name</Label><Input placeholder="User Login" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="space-y-1.5">
        <Label>Event Key</Label><Input placeholder="user_login" value={eventKey} onChange={(e) => setEventKey(e.target.value)} />
        <p className="text-[11px] text-muted-foreground">A unique identifier used to match this event in your data</p>
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <textarea className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Describe what this event represents" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </div>
      <SearchableSelect label="Entity Relation" value={entityRelation} onChange={(v) => setEntityRelation(v || "users")} options={[{ value: "users", label: "Users" }, { value: "accounts", label: "Accounts" }, { value: "companies", label: "Companies" }]} />
      <EditorFooter item={item} onDelete={onDelete} onCancel={onCancel} onSave={() => onSave(name, eventKey, description, entityRelation)} disabled={!name.trim() || !eventKey.trim()} />
    </div>
  );
};

const IndividualFeatureEditor = ({ item, onSave, onDelete, onCancel, events, products }: {
  item?: any;
  onSave: (name: string, description: string, metric_mappings: any[], product_ids: string[]) => void;
  onDelete: () => void;
  onCancel: () => void;
  events: EventCatalogItem[];
  products: Product[];
}) => {
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [metricMappings, setMetricMappings] = useState<any[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>(item?.product_ids || []);

  useEffect(() => { setName(item?.name || ""); setDescription(item?.description || ""); }, [item?.name, item?.description]);

  const workspace = useWorkspaceStore((state) => state.workspace);
  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({ workspaceId: workspace?.id, enabled: !!workspace?.id });
  const accountMetrics = workspaceSchema?.metricsByObjectSlug?.accounts || workspaceSchema?.metrics || { list: [], obj: {} };
  const userMetrics = workspaceSchema?.metricsByObjectSlug?.users || workspaceSchema?.metrics || { list: [], obj: {} };
  const metricOptions = useMemo(() => {
    const seen = new Set<string>(); const out: ComboOption[] = [];
    [...(accountMetrics?.list || []), ...(userMetrics?.list || [])].forEach((m: any) => {
      const val = m?.name as string; if (!val || seen.has(val)) return; seen.add(val);
      out.push({ value: val, label: m?.display_name || val });
    });
    return out;
  }, [accountMetrics?.list, userMetrics?.list]);
  const productOptions = useMemo(() => (products || []).filter((p) => !!p?.id).map((p) => ({ value: p.id!, label: p.name || p.id!.slice(0, 8) })), [products]);
  const frequencyOptions: ComboOption[] = [
    { value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" }, { value: "ad-hoc", label: "Ad-hoc" },
  ];

  useEffect(() => {
    if (item?.event_mappings && Array.isArray(item.event_mappings)) {
      setMetricMappings(item.event_mappings.map((em: any) => {
        const ev = (events || []).find((e) => e.id === em.event_id);
        return { mapping_id: em?.id, metric_key: ev?.event_key || undefined, description: ev?.description || undefined, weight: typeof em?.weight === "number" ? em.weight : undefined, expected_frequency: em?.expected_frequency };
      }));
    } else { setMetricMappings([]); }
    setSelectedProductIds(Array.from(new Set(item?.product_ids || [])));
  }, [item?.id, item?.event_mappings, item?.product_ids, events]);

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Features represent capabilities of your product. Map them to events and metrics to let AI understand feature engagement.</p>
      <div className="space-y-1.5"><Label>Feature Name</Label><Input placeholder="AI-Powered Recommendations" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Describe this feature and its benefits" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
      </div>
      <MultiCombobox label="Products" placeholder="Select products this feature belongs to" value={selectedProductIds} onChange={(vals) => setSelectedProductIds(Array.from(new Set(vals)))} options={productOptions} />
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">Mapped Metrics</p><p className="text-[11px] text-muted-foreground">Link metrics that indicate feature usage</p></div>
          <Button variant="outline" size="sm" onClick={() => setMetricMappings([...metricMappings, { metric_key: undefined }])}><Plus className="mr-1 h-3.5 w-3.5" />Add mapping</Button>
        </div>
        {metricMappings.map((m, idx) => (
          <div key={`mapping-${idx}`} className="border border-border rounded-md p-3 bg-muted/30 space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <SearchableSelect placeholder="Select metric" value={m?.metric_key || null} onChange={(val) => { const next = [...metricMappings]; next[idx] = { ...next[idx], metric_key: val }; setMetricMappings(next); }} options={metricOptions} />
              <div className="space-y-1.5"><Input placeholder="Description (optional)" className="h-8 text-xs" value={m?.description || ""} onChange={(e) => { const next = [...metricMappings]; next[idx] = { ...next[idx], description: e.target.value || undefined }; setMetricMappings(next); }} /></div>
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <div className="w-48"><SearchableSelect placeholder="Frequency" value={m?.expected_frequency ?? null} onChange={(val) => { const next = [...metricMappings]; next[idx] = { ...next[idx], expected_frequency: val || undefined }; setMetricMappings(next); }} options={frequencyOptions} clearable /></div>
              <Input type="number" placeholder="Weight" className="h-8 w-28 text-xs" min={0} step={0.1} value={m?.weight ?? ""} onChange={(e) => { const next = [...metricMappings]; const parsed = parseFloat(e.target.value); next[idx] = { ...next[idx], weight: Number.isNaN(parsed) ? undefined : parsed }; setMetricMappings(next); }} />
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => { const next = [...metricMappings]; next.splice(idx, 1); setMetricMappings(next); }}>Remove</Button>
            </div>
          </div>
        ))}
      </div>
      <EditorFooter item={item} onDelete={onDelete} onCancel={onCancel} onSave={() => onSave(name, description, metricMappings, selectedProductIds)} disabled={!name.trim()} />
    </div>
  );
};

const IndividualGoalEditor = ({ item, onSave, onDelete, onCancel, events }: {
  item?: any;
  onSave: (name: string, description: string, logic_type: "ALL" | "ANY" | "AT_LEAST_N", time_window_days: number | null, threshold: number | null, required_events: { event_id?: string; order_index?: number | null }[]) => void;
  onDelete: () => void;
  onCancel: () => void;
  events: EventCatalogItem[];
}) => {
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [logicType, setLogicType] = useState<"ALL" | "ANY" | "AT_LEAST_N">(item?.logic_type || "ALL");
  const [timeWindowDays, setTimeWindowDays] = useState<number>(item?.time_window_days ?? 30);
  const [threshold, setThreshold] = useState<number>(item?.threshold ?? 1);
  const [requiredEvents, setRequiredEvents] = useState<{ event_id?: string; order_index?: number | null }[]>(item?.required_events || []);

  useEffect(() => {
    setName(item?.name || ""); setDescription(item?.description || "");
    setLogicType(item?.logic_type || "ALL"); setTimeWindowDays(item?.time_window_days ?? 30);
    setThreshold(item?.threshold ?? 1); setRequiredEvents(item?.required_events || []);
  }, [item?.name, item?.description, item?.logic_type, item?.time_window_days, item?.threshold, item?.required_events]);

  const eventOptions = useMemo(() => (events || []).filter((e) => !!e?.id).map((e) => ({ value: e.id!, label: e.name || e.event_key || e.id!.slice(0, 8) })), [events]);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5"><Label>Goal Name</Label><Input placeholder="Onboarding complete" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Describe this business goal" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SearchableSelect label="Logic Type" value={logicType} onChange={(v) => setLogicType((v as any) || "ALL")} options={[{ value: "ALL", label: "ALL" }, { value: "ANY", label: "ANY" }, { value: "AT_LEAST_N", label: "AT_LEAST_N" }]} />
        <div className="space-y-1.5"><Label>Time Window (days)</Label><Input type="number" min={1} value={timeWindowDays} onChange={(e) => setTimeWindowDays(parseInt(e.target.value) || 0)} /></div>
        {logicType === "AT_LEAST_N" && (
          <div className="space-y-1.5"><Label>Threshold (N)</Label><Input type="number" min={1} value={threshold} onChange={(e) => setThreshold(parseInt(e.target.value) || 1)} /></div>
        )}
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium">Required Events (ordered)</p>
        {requiredEvents.map((re, idx) => (
          <div key={`req-${idx}`} className="flex gap-2 items-end">
            <div className="flex-1"><SearchableSelect placeholder="Select event" value={re?.event_id || null} onChange={(val) => { const next = [...requiredEvents]; next[idx] = { ...next[idx], event_id: val || undefined }; setRequiredEvents(next); }} options={eventOptions} /></div>
            <Input type="number" className="w-20 h-8" min={1} placeholder="Order" value={re?.order_index ?? idx + 1} onChange={(e) => { const next = [...requiredEvents]; next[idx] = { ...next[idx], order_index: parseInt(e.target.value) || idx + 1 }; setRequiredEvents(next); }} />
            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => { const next = [...requiredEvents]; next.splice(idx, 1); setRequiredEvents(next); }}>Remove</Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => setRequiredEvents([...requiredEvents, { event_id: undefined, order_index: requiredEvents.length + 1 }])}>
          <Plus className="mr-1 h-3.5 w-3.5" />Add Required Event
        </Button>
      </div>
      <EditorFooter item={item} onDelete={onDelete} onCancel={onCancel} onSave={() => onSave(name, description, logicType, timeWindowDays, logicType === "AT_LEAST_N" ? (threshold || 1) : null, requiredEvents)} disabled={!name.trim()} />
    </div>
  );
};

const IndividualLifecycleEditor = ({ item, onSave, onDelete, onCancel }: {
  item?: any;
  onSave: (name: string, description: string, color: string, filter: any) => void;
  onDelete: () => void;
  onCancel: () => void;
}) => {
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [color, setColor] = useState<string>(item?.color || "#3b82f6");
  const [entityRelation, setEntityRelation] = useState<"accounts" | "users">(
    (item?.entity_relation === "users" || item?.entity_relation === "accounts") ? item.entity_relation : "accounts",
  );

  useEffect(() => {
    setName(item?.name || ""); setDescription(item?.description || "");
    setColor(item?.color || "#3b82f6");
    setEntityRelation((item?.entity_relation === "users" || item?.entity_relation === "accounts") ? item.entity_relation : "accounts");
  }, [item?.name, item?.description, item?.color, item?.entity_relation]);

  const initialFilter = useMemo(() => {
    if (item?.filter && item.filter.predicates) return item.filter;
    return { query_type: "AND", predicates: [{ predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] }] };
  }, [item?.filter]);
  const [filterState, setFilterState] = useState<any>(initialFilter);

  useEffect(() => {
    try {
      const groups = (item?.filter?.predicates || []) as any[];
      const fields: string[] = [];
      groups.forEach((g: any) => { (g?.predicates || []).forEach((p: any) => { if (typeof p?.field === "string") fields.push(p.field); }); });
      if (fields.some((f) => f.startsWith("users."))) setEntityRelation("users");
      else if (fields.some((f) => f.startsWith("accounts."))) setEntityRelation("accounts");
    } catch { /* ignore */ }
  }, [item?.id]);

  const handleFilterAdd = () => {
    const groups = filterState?.predicates || [];
    const last = groups[groups.length - 1]?.predicates || [];
    if (last.length === 0 || last[0].field !== "") {
      setFilterState((prev: any) => ({ ...prev, predicates: [...prev.predicates, { predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] }] }));
    }
  };
  const handlePredicateAdd = (filterId?: number) => {
    const groups = filterState?.predicates || [];
    const selected = groups[filterId!]?.predicates || [];
    if (selected.length === 0 || selected[selected.length - 1].field !== "") {
      const next = [...groups];
      next[filterId!] = { ...next[filterId!], predicates: [...selected, { field: "", operator: "", value: "", type: "", entity_type: "column" }] };
      setFilterState((prev: any) => ({ ...prev, predicates: next }));
    }
  };
  const handlePredicateRemove = (id: number, filterId?: number) => {
    const groups = filterState?.predicates || [];
    const group = groups[filterId!]; if (!group) return;
    const nextGroup = { ...group, predicates: group.predicates.filter((_: any, idx: number) => idx !== id) };
    let nextGroups = [...groups];
    if (nextGroup.predicates.length === 0) { nextGroups = nextGroups.filter((_: any, idx: number) => idx !== filterId); }
    else { nextGroups[filterId!] = nextGroup; }
    setFilterState({ ...filterState, predicates: nextGroups.length ? nextGroups : [{ predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] }] });
  };
  const handlePredicateChange = (field: string, operator: string, value: any, type: string, entity_type?: string, id?: any, filterId?: any, time_window?: string) => {
    const groups = filterState?.predicates || []; const group = groups[filterId]; if (!group) return;
    const nextPreds = [...group.predicates];
    nextPreds[id] = { field, operator, value, type, entity_type: entity_type || "column", time_window };
    const nextGroups = [...groups]; nextGroups[filterId] = { ...group, predicates: nextPreds };
    setFilterState({ ...filterState, predicates: nextGroups });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">Lifecycle stages define the journey your customers go through. Use filter conditions to automatically classify accounts or users.</p>
      <div className="space-y-1.5"><Label>Stage Name</Label><Input placeholder="Onboarding" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Describe this lifecycle stage" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
      </div>
      <ColorField label="Stage Color" value={color} onChange={setColor} />
      <SearchableSelect label="Filter Entity Context" value={entityRelation} onChange={(v) => setEntityRelation((v as "accounts" | "users") || "accounts")} options={[{ value: "accounts", label: "Accounts" }, { value: "users", label: "Users" }]} />
      <p className="text-xs text-muted-foreground">Define conditions that determine when an account or user enters this stage.</p>
      <div className="bg-muted/30 p-2 rounded-md overflow-x-auto">
        {(filterState?.predicates || []).map((filter: any, idx: number) => (
          <div key={`lf-${idx}`} className="min-w-[720px]">
            <SignalFilterContainer selectedFilters={filter.predicates} handleFilter={handlePredicateChange} handleRemove={handlePredicateRemove} handleAdd={handlePredicateAdd} filterId={idx} entityRelation={entityRelation} />
            {idx < (filterState?.predicates?.length || 0) - 1 && <p className="text-xs text-muted-foreground text-center">- OR -</p>}
          </div>
        ))}
        <Button variant="ghost" size="sm" className="mt-2" onClick={handleFilterAdd}><Plus className="mr-1 h-3.5 w-3.5" />Add Condition Group</Button>
      </div>
      <EditorFooter item={item} onDelete={onDelete} onCancel={onCancel} onSave={() => onSave(name, description, color, filterState)} disabled={!name.trim()} />
    </div>
  );
};

const IndividualICPEditor = ({ item, onSave, onDelete, onCancel, features }: {
  item?: any;
  onSave: (name: string, description: string, fit_filter: any, mapped_feature_ids: string[], expansion_levers: string[]) => void;
  onDelete: () => void;
  onCancel: () => void;
  features: Feature[];
}) => {
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [entityRelation, setEntityRelation] = useState<"accounts" | "users">(
    (item?.fit_filter?.entity_relation === "users" || item?.fit_filter?.entity_relation === "accounts") ? item.fit_filter.entity_relation : "accounts",
  );
  useEffect(() => { setName(item?.name || ""); setDescription(item?.description || ""); }, [item?.name, item?.description]);

  const initialFilter = useMemo(() => {
    const base = item?.fit_filter && item.fit_filter.predicates ? item.fit_filter : { query_type: "AND", predicates: [{ predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] }] };
    return { entity_relation: entityRelation, ...base };
  }, [item?.fit_filter, entityRelation]);
  const [filterState, setFilterState] = useState<any>(initialFilter);
  const [mappedFeatureIds, setMappedFeatureIds] = useState<string[]>(item?.mapped_feature_ids || []);
  const [expansionLevers, setExpansionLevers] = useState<string[]>(item?.expansion_levers || []);

  useEffect(() => { setFilterState(initialFilter); setMappedFeatureIds(item?.mapped_feature_ids || []); setExpansionLevers(item?.expansion_levers || []); }, [initialFilter, item?.id]);

  const featureOptions = useMemo(() => (features || []).filter((f) => !!f?.id).map((f) => ({ value: f.id!, label: f.name || f.id!.slice(0, 8) })), [features]);

  const handleFilterAdd = () => {
    setFilterState((prev: any) => {
      const groups = prev?.predicates || []; const last = groups[groups.length - 1]?.predicates || [];
      if (last.length !== 0 && last[0].field === "") return prev;
      return { ...prev, predicates: [...groups, { predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] }] };
    });
  };
  const handlePredicateAdd = (filterId?: number) => {
    setFilterState((prev: any) => {
      const groups = prev?.predicates || []; const selected = groups[filterId!]?.predicates || [];
      if (selected.length !== 0 && selected[selected.length - 1].field === "") return prev;
      const next = [...groups]; next[filterId!] = { ...next[filterId!], predicates: [...selected, { field: "", operator: "", value: "", type: "", entity_type: "column" }] };
      return { ...prev, predicates: next };
    });
  };
  const handlePredicateRemove = (id: number, filterId?: number) => {
    setFilterState((prev: any) => {
      const groups = prev?.predicates || []; const group = groups[filterId!]; if (!group) return prev;
      const nextGroup = { ...group, predicates: group.predicates.filter((_: any, idx: number) => idx !== id) };
      let nextGroups = [...groups];
      if (nextGroup.predicates.length === 0) { nextGroups = nextGroups.filter((_: any, idx: number) => idx !== filterId); }
      else { nextGroups[filterId!] = nextGroup; }
      return { ...prev, predicates: nextGroups.length ? nextGroups : [{ predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] }] };
    });
  };
  const handlePredicateChange = (field: string, operator: string, value: any, type: string, entity_type?: string, id?: any, filterId?: any, time_window?: string) => {
    setFilterState((prev: any) => {
      const groups = prev?.predicates || []; const group = groups[filterId]; if (!group) return prev;
      const nextPreds = [...group.predicates];
      nextPreds[id] = { field, operator, value, type, entity_type: entity_type || "column", time_window };
      const nextGroups = [...groups]; nextGroups[filterId] = { ...group, predicates: nextPreds };
      return { ...prev, predicates: nextGroups };
    });
  };
  const sanitizeFitFilter = (fs: any) => {
    const qtype = fs?.query_type || "AND";
    const groups = Array.isArray(fs?.predicates) ? fs.predicates : [];
    const cleanedGroups = groups.map((g: any) => ({
      predicates: (g?.predicates || []).filter((p: any) => p && typeof p.field === "string" && p.field.trim() && typeof p.operator === "string" && p.operator.trim()).map((p: any) => ({ field: p.field, operator: p.operator, value: p.value, type: p.type, entity_type: p.entity_type || "column", ...(p.time_window ? { time_window: p.time_window } : {}) })),
    })).filter((g: any) => g.predicates.length > 0);
    return { entity_relation: entityRelation, query_type: qtype, predicates: cleanedGroups };
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5"><Label>Archetype Name</Label><Input placeholder="High-Growth SaaS Company" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="Describe this ideal customer profile" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
      </div>
      <SearchableSelect label="Fit Filter Entity Context" value={entityRelation} onChange={(v) => setEntityRelation((v as "accounts" | "users") || "accounts")} options={[{ value: "accounts", label: "Accounts" }, { value: "users", label: "Users" }]} />
      <div className="bg-muted/30 p-2 rounded-md overflow-x-auto">
        {(filterState?.predicates || []).map((filter: any, idx: number) => (
          <div key={`icpf-${idx}`} className="min-w-[720px]">
            <SignalFilterContainer selectedFilters={filter.predicates} handleFilter={handlePredicateChange} handleRemove={handlePredicateRemove} handleAdd={handlePredicateAdd} filterId={idx} entityRelation={entityRelation} />
            {idx < (filterState?.predicates?.length || 0) - 1 && <p className="text-xs text-muted-foreground text-center">- OR -</p>}
          </div>
        ))}
        <Button variant="ghost" size="sm" className="mt-2" onClick={handleFilterAdd}><Plus className="mr-1 h-3.5 w-3.5" />Add Condition Group</Button>
      </div>
      <MultiCombobox label="Mapped Features" placeholder="Select relevant features" value={mappedFeatureIds} onChange={setMappedFeatureIds} options={featureOptions} />
      <TagsField label="Expansion Levers" placeholder="e.g. invite more team members" value={expansionLevers} onChange={setExpansionLevers} />
      <EditorFooter item={item} onDelete={onDelete} onCancel={onCancel} onSave={() => onSave(name, description, sanitizeFitFilter(filterState), mappedFeatureIds, expansionLevers)} disabled={!name.trim()} />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Company Profile Editor                                            */
/* ------------------------------------------------------------------ */

const CompanyProfileEditor = ({ value, onChange }: { value: any; onChange: (next: any) => void }) => {
  const [companyName, setCompanyName] = useState<string>(value?.company_name ?? "");
  const [website, setWebsite] = useState<string>(value?.website ?? "");
  const [description, setDescription] = useState<string>(value?.description ?? "");
  const [targetMarkets, setTargetMarkets] = useState<string[]>(value?.target_markets ?? []);
  const [valueProps, setValueProps] = useState<any[]>(value?.value_props ?? []);

  useEffect(() => {
    setCompanyName(value?.company_name ?? ""); setWebsite(value?.website ?? "");
    setDescription(value?.description ?? ""); setTargetMarkets(value?.target_markets ?? []);
    setValueProps(value?.value_props ?? []);
  }, [value?.company_name, value?.website, value?.description, value?.target_markets, value?.value_props]);

  const emit = useCallback((patch: any) => onChange({ company_name: companyName, website, description, target_markets: targetMarkets, value_props: valueProps, ...patch }), [companyName, website, description, targetMarkets, valueProps, onChange]);

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/50 px-3 py-2">
        <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">We&apos;ve used AI to take a first pass at your company profile. Edit the fields below to help us better understand your business.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Company Name</Label><Input placeholder="HyperEngage" value={companyName} onChange={(e) => { setCompanyName(e.target.value); emit({ company_name: e.target.value }); }} /></div>
        <div className="space-y-1.5"><Label>Company Website</Label><Input placeholder="hyperengage.io" value={website} onChange={(e) => { setWebsite(e.target.value); emit({ website: e.target.value }); }} /></div>
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <p className="text-[11px] text-muted-foreground">What does your company do? What types of customers do you serve?</p>
        <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" placeholder="A short blurb about your company..." rows={4} value={description} onChange={(e) => { setDescription(e.target.value); emit({ description: e.target.value }); }} />
      </div>
      <div className="space-y-1.5">
        <Label>Target Markets & ICP</Label>
        <p className="text-[11px] text-muted-foreground">Describe the typical leads you focus on. What roles, departments, and goals do they have?</p>
        <TagsField placeholder="Revenue Operations, Marketing Operations, Customer Success leaders" value={targetMarkets} onChange={(vals) => { setTargetMarkets(vals); emit({ target_markets: vals }); }} />
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main page component                                               */
/* ------------------------------------------------------------------ */

type SectionKey = "company-profile" | "products" | "features" | "events" | "lifecycle" | null;

const SECTION_HELP: Record<string, string> = {
  "company-profile": "Tell us about your company so AI can personalize insights and recommendations for your team.",
  products: "Add the products or services you offer. This helps AI understand your catalog when analyzing customer behavior.",
  features: "Map your product features to events and metrics. AI uses these mappings to understand feature adoption and engagement.",
  events: "Define the key events that track user and account activity. Events are the building blocks for features, goals, and lifecycle stages.",
  lifecycle: "Define the stages customers go through in their journey. Use filter conditions to automatically classify accounts or users.",
};

interface BusinessContextPageProps {
  section?: SectionKey;
  embedded?: boolean;
}

const BusinessContextPage = ({ section: sectionProp, embedded }: BusinessContextPageProps = {}) => {
  const [searchParams] = useSearchParams();
  const sectionParam = sectionProp || (searchParams.get("section") as SectionKey) || null;
  const workspaceId = useWorkspaceStore((state) => state.workspace?.id);
  const { data: workspaceSchema }: any = useWorkspaceObjectSchema({ workspaceId, enabled: !!workspaceId });
  const accountMetrics = workspaceSchema?.metricsByObjectSlug?.accounts || workspaceSchema?.metrics || { list: [], obj: {} };
  const userMetrics = workspaceSchema?.metricsByObjectSlug?.users || workspaceSchema?.metrics || { list: [], obj: {} };
  const { data, isLoading, error } = useBusinessContext(workspaceId);
  const update = useUpdateBusinessContext(workspaceId);
  const [companyDraft, setCompanyDraft] = useState<any>();
  const [productsDraft, setProductsDraft] = useState<Product[]>([]);
  const [eventsDraft, setEventsDraft] = useState<EventCatalogItem[]>([]);
  const [featuresDraft, setFeaturesDraft] = useState<Feature[]>([]);
  const [goalsDraft, setGoalsDraft] = useState<Goal[]>([]);
  const [lifecycleDraft, setLifecycleDraft] = useState<LifecycleStage[]>([]);
  const [icpDraft, setIcpDraft] = useState<ICPArchetype[]>([]);
  const [dirty, setDirty] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<{
    title: string;
    type: "valueProps" | "products" | "events" | "features" | "goals" | "lifecycle" | "icp";
    editingId?: string;
    editingItem?: any;
  } | null>(null);

  const openModal = (type: typeof modalContent extends null ? never : NonNullable<typeof modalContent>["type"], editingId?: string) => {
    const titles: Record<string, string> = { valueProps: "Value Proposition", products: "Product", events: "Event", features: "Feature", goals: "Goal", lifecycle: "Lifecycle Stage", icp: "ICP Archetype" };
    let editingItem = null;
    if (editingId) {
      if (type === "valueProps") editingItem = (companyDraft?.value_props ?? []).find((vp: any) => vp.id === editingId);
      else if (type === "products") editingItem = productsDraft.find((p) => p.id === editingId);
      else if (type === "events") editingItem = eventsDraft.find((e) => e.id === editingId);
      else if (type === "features") editingItem = featuresDraft.find((f) => f.id === editingId);
      else if (type === "goals") editingItem = goalsDraft.find((g) => g.id === editingId);
      else if (type === "lifecycle") editingItem = lifecycleDraft.find((l) => l.id === editingId);
      else if (type === "icp") editingItem = icpDraft.find((i) => i.id === editingId);
    }
    setModalContent({ title: editingId ? `Edit ${titles[type]}` : `Add New ${titles[type]}`, type, editingId, editingItem });
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setModalContent(null); };
  const has403 = (error as any)?.response?.status === 403;

  useEffect(() => { document.title = "Business Context | Hyperengage"; }, []);
  useEffect(() => {
    if (data) {
      setCompanyDraft(data.company_profile ?? {}); setProductsDraft(data.products ?? []);
      setEventsDraft(data.event_catalog ?? []); setFeaturesDraft(data.features ?? []);
      setGoalsDraft(data.goals ?? []); setLifecycleDraft(data.lifecycle_stages ?? []);
      setIcpDraft(data.icp_archetypes ?? []); setDirty(false);
    }
  }, [data]);

  const patch = (payload: BusinessContextPatch) => {
    update.mutate(payload, {
      onError: (err: any) => {
        if (err?.response?.status === 403) { notify({ heading: "Permission denied", type: "failure", messageList: ["You don't have permission to edit Business Context."] }); return; }
        notify({ heading: "Update failed", type: "failure", messageList: ["Could not save changes. Please try again."] });
      },
      onSuccess: (updatedData) => {
        notify({ heading: "Saved", type: "success", messageList: ["Business Context updated"] });
        if (updatedData) {
          setCompanyDraft(updatedData.company_profile ?? {}); setProductsDraft(updatedData.products ?? []);
          setEventsDraft(updatedData.event_catalog ?? []); setFeaturesDraft(updatedData.features ?? []);
          setGoalsDraft(updatedData.goals ?? []); setLifecycleDraft(updatedData.lifecycle_stages ?? []);
          setIcpDraft(updatedData.icp_archetypes ?? []); setDirty(false);
        }
      },
    });
  };

  const onCompanyChange = (next: any) => { setCompanyDraft(next); setDirty(true); };
  const handleRemoveValueProp = (id: string) => { setCompanyDraft((prev: any) => ({ ...prev, value_props: (prev?.value_props ?? []).filter((vp: any) => vp.id !== id) })); setDirty(true); };

  const saveAll = () => {
    const cleanedEvents = eventsDraft.filter((e) => e.id && (e.name?.trim() || e.event_key?.trim()));
    const sanitizedEvents = cleanedEvents.map((ev) => {
      const allowed = ["users", "accounts", "companies"];
      return { ...ev, entity_relation: allowed.includes((ev as any)?.entity_relation) ? (ev as any).entity_relation : "accounts" } as EventCatalogItem;
    });
    patch({ company_profile: companyDraft, products: productsDraft, event_catalog: sanitizedEvents, features: featuresDraft, goals: goalsDraft, lifecycle_stages: lifecycleDraft, icp_archetypes: icpDraft });
  };

  const discardAll = () => {
    if (data) {
      setCompanyDraft(data.company_profile ?? {}); setProductsDraft(data.products ?? []);
      setEventsDraft(data.event_catalog ?? []); setFeaturesDraft(data.features ?? []);
      setGoalsDraft(data.goals ?? []); setLifecycleDraft(data.lifecycle_stages ?? []);
      setIcpDraft(data.icp_archetypes ?? []);
    }
    setDirty(false);
  };

  if (isLoading) return <div className={embedded ? "flex items-center justify-center py-12" : "mx-auto max-w-5xl px-6 py-12 flex justify-center"}><span className="h-6 w-6 animate-spin rounded-full border-2 border-primary/30 border-t-primary" /></div>;
  if (has403) return <div className={embedded ? "py-8 text-center" : "mx-auto max-w-5xl px-6 py-12 text-center"}><p className="text-sm font-medium">Business Context</p><p className="mt-1 text-xs text-muted-foreground">You don&apos;t have permission to view Business Context.</p></div>;
  if (error) return <div className={embedded ? "py-8 text-center" : "mx-auto max-w-5xl px-6 py-12 text-center"}><p className="text-sm font-medium">Business Context</p><p className="mt-1 text-xs text-muted-foreground">Failed to load. Please try again.</p></div>;

  const showSection = (key: SectionKey) => !sectionParam || sectionParam === key;

  return (
    <div className={embedded ? "" : "min-h-[calc(100vh-100px)] bg-muted/30"}>
      <div className={embedded ? "" : "mx-auto max-w-5xl px-6 py-4"}>
        {sectionParam && SECTION_HELP[sectionParam] && (
          <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/50 px-3 py-2 mb-4">
            <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">{SECTION_HELP[sectionParam]}</p>
          </div>
        )}
        <div className="space-y-6">
          {showSection("company-profile") && (
            <>
              <CompanyProfileEditor value={companyDraft} onChange={onCompanyChange} />
              <PreviewCard title="Value Propositions" subtitle="Short statements that highlight why customers choose you over alternatives." items={(companyDraft?.value_props ?? []).map((vp: any) => ({ title: vp.title || "Untitled", subtitle: vp.description || "No description", id: vp.id }))} addButtonText="Add new Value Proposition" onEdit={(id) => openModal("valueProps", id)} borderColor="#93c5fd" />
            </>
          )}
          {showSection("products") && <PreviewCard title="Products" subtitle="What do you sell?" items={productsDraft.map((p) => ({ title: p.name || "Unnamed Product", subtitle: "Product configuration and details", id: p.id }))} addButtonText="Add new Product" onEdit={(id) => openModal("products", id)} borderColor="#86efac" />}
          {showSection("events") && <PreviewCard title="Event Catalog" subtitle="Define events that track user and account activity." items={eventsDraft.map((e) => ({ title: e.name || "Unnamed Event", subtitle: e.description || e.event_key || "No description", badge: e.entity_relation || undefined, id: e.id }))} addButtonText="Add new Event" onEdit={(id) => openModal("events", id)} borderColor="#fbbf24" />}
          {showSection("features") && <PreviewCard title="Features" subtitle="Map your product features to events and define their relationships." items={featuresDraft.filter((f) => f?.is_active !== false).map((f) => ({ title: f.name || "Unnamed Feature", subtitle: f.description || "No description", badge: f.is_active ? "Active" : "Inactive", id: f.id }))} addButtonText="Add new Feature" onEdit={(id) => openModal("features", id)} borderColor="#a78bfa" />}
          {showSection("lifecycle") && <PreviewCard title="Lifecycle Stages" subtitle="Define the stages in your customer lifecycle and their progression rules." items={lifecycleDraft.map((s) => ({ title: s.name || "Unnamed Stage", subtitle: s.description || "No description", badge: s.sort_order ? `Order: ${s.sort_order}` : undefined, id: s.id }))} addButtonText="Add new Stage" onEdit={(id) => openModal("lifecycle", id)} borderColor="#38bdf8" />}
        </div>

        {dirty && (
          <div className="fixed bottom-6 right-6 z-50">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-3 shadow-lg">
              <Button variant="outline" size="sm" onClick={discardAll} disabled={update.isLoading}>Discard Changes</Button>
              <Button size="sm" onClick={saveAll} disabled={update.isLoading}>
                {update.isLoading && <span className="mr-1.5 h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />}
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          onPointerDownOutside={(e) => {
            // Prevent Radix Dialog from intercepting clicks on portaled Mantine dropdowns
            const target = e.target as HTMLElement;
            if (target?.closest?.(".mantine-Menu-dropdown, .mantine-Popover-dropdown, .mantine-Select-dropdown, [data-mantine-portal]")) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target?.closest?.(".mantine-Menu-dropdown, .mantine-Popover-dropdown, .mantine-Select-dropdown, [data-mantine-portal]")) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader><DialogTitle>{modalContent?.title}</DialogTitle></DialogHeader>
          <div>
            {modalContent?.type === "valueProps" && <IndividualValuePropEditor item={modalContent.editingItem} onSave={(title, desc) => { if (modalContent.editingId) { onCompanyChange({ ...companyDraft, value_props: (companyDraft?.value_props ?? []).map((vp: any) => vp.id === modalContent.editingId ? { ...vp, title, description: desc } : vp) }); } else { onCompanyChange({ ...companyDraft, value_props: [...(companyDraft?.value_props ?? []), { id: v4(), title, description: desc }] }); } closeModal(); }} onDelete={() => { if (modalContent.editingId) { handleRemoveValueProp(modalContent.editingId); closeModal(); } }} onCancel={closeModal} />}
            {modalContent?.type === "products" && <IndividualProductEditor item={modalContent.editingItem} onSave={(name) => { if (modalContent.editingId) { setProductsDraft((prev) => prev.map((p) => p.id === modalContent.editingId ? { ...p, name } : p)); } else { setProductsDraft((prev) => [...prev, { id: v4(), name, pricing_plans: [] }]); } setDirty(true); closeModal(); }} onDelete={() => { if (modalContent.editingId) { setProductsDraft((prev) => prev.filter((p) => p.id !== modalContent.editingId)); setDirty(true); closeModal(); } }} onCancel={closeModal} />}
            {modalContent?.type === "events" && <IndividualEventEditor item={modalContent.editingItem} onSave={(name, key, desc, entity) => { if (modalContent.editingId) { setEventsDraft((prev) => prev.map((e) => e.id === modalContent.editingId ? { ...e, name, event_key: key, description: desc, entity_relation: entity } : e)); } else { setEventsDraft((prev) => [...prev, { id: v4(), name, event_key: key, description: desc, entity_relation: entity } as EventCatalogItem]); } setDirty(true); closeModal(); }} onDelete={() => { if (modalContent.editingId) { setEventsDraft((prev) => prev.filter((e) => e.id !== modalContent.editingId)); setDirty(true); closeModal(); } }} onCancel={closeModal} />}
            {modalContent?.type === "features" && <IndividualFeatureEditor item={modalContent.editingItem} events={eventsDraft} products={productsDraft} onSave={(name, desc, metric_mappings, product_ids) => {
              const ensureEventId = (metricKey: string, evDesc?: string) => {
                const existing = eventsDraft.find((e) => e.event_key === metricKey);
                if (existing?.id) return existing.id;
                const newId = v4();
                let inferredEntity: "users" | "accounts" | "companies" = "accounts";
                if (accountMetrics?.obj?.[metricKey]) inferredEntity = "accounts";
                else if (userMetrics?.obj?.[metricKey]) inferredEntity = "users";
                setEventsDraft((prev) => [...prev, { id: newId, name: metricKey, ...(evDesc ? { description: evDesc } : {}), event_key: metricKey, entity_relation: inferredEntity } as EventCatalogItem]);
                return newId;
              };
              const eventMappings = (metric_mappings || []).filter((m: any) => !!m.metric_key).map((m: any) => {
                const mapping: any = { id: m?.mapping_id || v4(), event_id: ensureEventId(m.metric_key, m.description) };
                if (typeof m.weight === "number" && !Number.isNaN(m.weight)) mapping.weight = m.weight;
                if (m.expected_frequency) mapping.expected_frequency = m.expected_frequency;
                return mapping;
              });
              if (modalContent.editingId) { setFeaturesDraft((prev) => prev.map((f) => f.id === modalContent.editingId ? { ...f, name, description: desc, event_mappings: eventMappings, product_ids } : f)); }
              else { setFeaturesDraft((prev) => [...prev, { id: v4(), name, description: desc, product_ids, tags: [], is_active: true, event_mappings: eventMappings } as Feature]); }
              setDirty(true); closeModal();
            }} onDelete={() => { if (modalContent.editingId) { setFeaturesDraft((prev) => prev.map((f) => f.id === modalContent.editingId ? { ...f, is_active: false } : f)); setDirty(true); closeModal(); } }} onCancel={closeModal} />}
            {modalContent?.type === "goals" && <IndividualGoalEditor item={modalContent.editingItem} events={eventsDraft} onSave={(name, desc, logic_type, time_window_days, threshold, required_events) => { if (modalContent.editingId) { setGoalsDraft((prev) => prev.map((g) => g.id === modalContent.editingId ? { ...g, name, description: desc, logic_type, time_window_days, threshold, required_events } : g)); } else { setGoalsDraft((prev) => [...prev, { id: v4(), name, description: desc, logic_type, threshold, time_window_days, required_events }]); } setDirty(true); closeModal(); }} onDelete={() => { if (modalContent.editingId) { setGoalsDraft((prev) => prev.filter((g) => g.id !== modalContent.editingId)); setDirty(true); closeModal(); } }} onCancel={closeModal} />}
            {modalContent?.type === "lifecycle" && <IndividualLifecycleEditor item={modalContent.editingItem} onSave={(name, desc, color, filter) => { if (modalContent.editingId) { setLifecycleDraft((prev) => prev.map((l) => l.id === modalContent.editingId ? { ...l, name, description: desc, color, filter } : l)); } else { setLifecycleDraft((prev) => [...prev, { id: v4(), name, description: desc, entity_relation: "companies", color, sort_order: prev.length + 1, filter } as LifecycleStage]); } setDirty(true); closeModal(); }} onDelete={() => { if (modalContent.editingId) { setLifecycleDraft((prev) => prev.filter((l) => l.id !== modalContent.editingId)); setDirty(true); closeModal(); } }} onCancel={closeModal} />}
            {modalContent?.type === "icp" && <IndividualICPEditor item={modalContent.editingItem} features={featuresDraft.filter((f) => f?.is_active !== false)} onSave={(name, desc, fit_filter, mapped_feature_ids, expansion_levers) => { const dedupFeatures = Array.from(new Set(mapped_feature_ids)); if (modalContent.editingId) { setIcpDraft((prev) => prev.map((i) => i.id === modalContent.editingId ? { ...i, name, description: desc, fit_filter, mapped_feature_ids: dedupFeatures, expansion_levers } : i)); } else { setIcpDraft((prev) => [...prev, { id: v4(), name, description: desc, fit_filter, mapped_feature_ids: dedupFeatures, expansion_levers }]); } setDirty(true); closeModal(); }} onDelete={() => { if (modalContent.editingId) { setIcpDraft((prev) => prev.filter((i) => i.id !== modalContent.editingId)); setDirty(true); closeModal(); } }} onCancel={closeModal} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessContextPage;
